import React, {useEffect, useState} from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import { SpaceBetween, Spinner } from "@amzn/awsui-components-react/polaris";
import { downloadGeneratedFileByTransactionId } from "./components/excaliburAPIs";
import {useReducer} from "react";
import { useNavigate } from "react-router-dom";
import {INITIAL_STATE, tableReducer, tableReducerInit} from "./components/excaliburTableReducer";
import {
    getFileNameButtonFromS3Key,
    getFileNameButtonFromDMS,
    statusColorCodes
} from "../../common/utils/commonUtils";
import EllipsisText from "../../common/ellipsisText/EllipsisText";
import WindowPagination from "../../common/pagination/WindowPagination";
import ColumnHeader from "../../common/table/ColumnHeader";
import StatusInfo from "../../common/table/StatusInfo";
import {EXCALIBUR_COLUMN_INFO, EXCALIBUR_STATUS_INFO} from "../../common/constants/columnInfo";
import {getPageItems} from "../../common/pagination/paging";
import Button from "@amzn/awsui-components-react/polaris/button";
import Link from "@amzn/awsui-components-react/polaris/link";
import { ROUTE_PATH_FILING_PAGE, ROUTE_PATH_EXCALIBUR_TRANSACTION_DETAILS_PAGE } from "../../common/constants/route_paths";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import {formatDateWithTime} from "../../common/utils/apiUtils";
import {uploadFiles, downloadFolder} from "../../common/utils/s3Utils";
import AppConfig from "../../AppConfig";
import { ARTIFACTS_FORMAT } from "../../common/constants/constants";
import { EXCALIBUR_STATUS, ERROR_REPORT_DOWNLOAD_FAILURE_FLASH } from "./constants/constants";

const Actiontable = (params) => {
    const {downloadDocumentDispatcher, refreshToken = 0} = params;

    //Table State Management Reducer
    const [table, tableDispatcher] = useReducer(tableReducer,
        INITIAL_STATE, tableReducerInit);
    const navigate = useNavigate();     

    //This is to initialize the first call
    useEffect(() => {
        if (table.initialize) {
            tableDispatcher({type: "INITIALIZE", payload: {dispatcher: tableDispatcher}});
        }
    }, [table.initialize]);

    // A new request was just created from the upload form: reload the list so its row shows up.
    useEffect(() => {
        if (refreshToken > 0) {
            tableDispatcher({type: "REFRESH", payload: {dispatcher: tableDispatcher}});
        }
    }, [refreshToken]);

    const [selectedItems, setSelectedItems] = useState([]);
    const [visible, setVisible] = useState(false);
    const [artifactLoading, setArtifactLoading] = useState(false);
    const [artifactFlash, setArtifactFlash] = useState(null);
    const [artifactKeysMap, setArtifactKeysMap] = useState({});

    const getFolderPath = (key) => {
        if (!key) return key;
        // Remove trailing slash if present
        const trimmedKey = key.endsWith('/') ? key.slice(0, -1) : key;
        // If key contains a dot after the last slash, it's a file path — extract the folder
        const lastSlashIndex = trimmedKey.lastIndexOf('/');
        const lastSegment = trimmedKey.substring(lastSlashIndex + 1);
        if (lastSegment.includes('.')) {
            return lastSlashIndex >= 0 ? trimmedKey.substring(0, lastSlashIndex) : trimmedKey;
        }
        return trimmedKey;
    }

    const downloadArtifacts = async (generatedFileKey) => {
        const folderKey = getFolderPath(generatedFileKey);
        setArtifactLoading(true);
        setArtifactFlash(null);
        try {
            await downloadFolder(folderKey, AppConfig[params.domain].ARTIFACTS_BUCKET, params.domain);
        } catch (error) {
            console.error("Error downloading artifacts:", error);
            setArtifactFlash({type: "error", message: "Error occurred while downloading artifacts"});
        }
        setArtifactLoading(false);
    }

    const uploadArtifacts = async (item, files) => {
        const folderKey = getFolderPath(item.generatedFileKey);
        setArtifactLoading(true);
        setArtifactFlash(null);
        try {
            await uploadFiles(files, AppConfig[params.domain].ARTIFACTS_BUCKET, folderKey, params.domain);

            // Track uploaded artifact S3 keys locally by transactionId
            const newKeys = Array.from(files).map(file => `${folderKey}/${file.name}`);
            setArtifactKeysMap(prev => ({
                ...prev,
                [item.transactionId]: [...(prev[item.transactionId] || []), ...newKeys]
            }));

            setArtifactFlash({type: "success", message: "Artifacts uploaded successfully"});
        } catch (error) {
            console.error("Error uploading artifacts:", error);
            setArtifactFlash({type: "error", message: "Error occurred while uploading artifacts"});
        }
        setArtifactLoading(false);
    }

    const onDownloadErrorReport = async (row) => {
        setArtifactFlash(null);
        try {
            await downloadGeneratedFileByTransactionId({
                useCase: row.useCase,
                subUseCase: row.subUseCase,
                transactionId: row.transactionId,
            });
        } catch (error) {
            console.error("Error downloading error report:", error);
            setArtifactFlash(ERROR_REPORT_DOWNLOAD_FAILURE_FLASH);
        }
    }

    const renderErrorDetails = (row) => {
        const hasErrorReport = row.status === EXCALIBUR_STATUS.FAILURE && Boolean(row.generatedFileKey);
        if (!hasErrorReport) {
            return <EllipsisText text={row.errorDetails} fill />;
        }
        return (
            <SpaceBetween direction="vertical" size="xxs">
                <EllipsisText text={row.errorDetails} fill />
                <Link onFollow={() => onDownloadErrorReport(row)}>
                    Download error report
                </Link>
            </SpaceBetween>
        );
    }

    const ERROR_STATUSES = [EXCALIBUR_STATUS.FAILURE, EXCALIBUR_STATUS.EXCEPTION];
    const hasError = (row) => ERROR_STATUSES.includes(row.status) || Boolean(row.errorDetails);

    const renderErrorDetailsWithMoreInfo = (row) => {
        if (!hasError(row)) {
            return renderErrorDetails(row);
        }
        return (
            <SpaceBetween direction="vertical" size="xxs">
                {renderErrorDetails(row)}
                <Link onFollow={() => navigateToTransactionDetailsPage(row)}>More Info</Link>
            </SpaceBetween>
        );
    }

    const onSelectionChange = (item) => {
        setSelectedItems(item.detail.selectedItems)
    }

    const handleEFileClick = () => {
        setVisible(true);
    }
    const handleDismissModal = () => {
        setVisible(false);
    }
    const navigateToTransactionDetailsPage = (transaction) => {
        navigate(ROUTE_PATH_EXCALIBUR_TRANSACTION_DETAILS_PAGE, {
            state: {
                data: transaction
            }
        });
    }
    const navigateToFiling = () => {
        const selected = selectedItems[0];
        navigate(ROUTE_PATH_FILING_PAGE, {
            state: {
                data: {
                    ...selected,
                    artifactKeys: artifactKeysMap[selected.transactionId] || [],
                    artifactBucket: AppConfig[params.domain].ARTIFACTS_BUCKET
                }
            }
        });
    }

    return (
        <>
        {artifactLoading && <Spinner size="large" />}
        {artifactFlash &&
            <Box margin={{bottom: "s"}}>
                <div style={{color: artifactFlash.type === "error" ? "red" : "green"}}>
                    {artifactFlash.message}
                </div>
            </Box>
        }
        <Modal
            onDismiss={handleDismissModal}
            visible={visible}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link">Cancel</Button>
                        <Button variant="primary" onClick={navigateToFiling}>Ok</Button>
                    </SpaceBetween>
                </Box>
            }
            header="Modal title"
        >
            Proceed To Filing?
        </Modal>
        <Table
            header={<Header
                variant="h2"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button disabled={selectedItems.length === 0 || selectedItems[0].status !== "SUCCESS"} onClick={handleEFileClick}>
                        Start E-File
                    </Button>
                    <WindowPagination
                        currentPageIndex={table.currentPageIndex}
                        pagesCount={table.pageCount}
                        openEnd={Boolean(table.lastEvaluatedKey)}
                        onChange={event => {
                            tableDispatcher({type:"PAGE_INDEX_CHANGED",
                                payload:{pageIndex:event.detail.currentPageIndex, dispatcher:tableDispatcher}})
                        }
                        }
                        ariaLabels={{
                            nextPageLabel: "Next page",
                            previousPageLabel: "Previous page",
                            pageLabel: pageNumber =>
                                `Page ${pageNumber} of all pages`
                        }}
                    />
                    <Button iconName="refresh" variant="icon"
                        onClick={()=>{
                            tableDispatcher({type:"REFRESH",payload:{dispatcher:tableDispatcher}})
                        }}
                    />
                  </SpaceBetween>
                }
              >Submitted Requests
              </Header>}
            loading={table.loading}
            selectionType="single"
            onSelectionChange={onSelectionChange}
            selectedItems={selectedItems}
            loadingText={table.loadingText}
            onSortingChange={((event)=>{
                    tableDispatcher({type:"SORTING_CHANGED",
                        payload:{isDescending:event.detail.isDescending,
                            sortingField:event.detail.sortingColumn.sortingField}});
            })}
            sortingColumn={table.sortingColumn}
            sortingDescending={table.sortingDescending}
            resizableColumns
            columnDefinitions={[
                {
                    id: "useCase",
                    header: <ColumnHeader title="Country" info={EXCALIBUR_COLUMN_INFO.useCase} />,
                    cell: e => e.useCase,
                    sortingField: "useCase",
                    width: 150,
                    minWidth: 150
                },
                {
                    id: "subUseCase",
                    header: <ColumnHeader title="Filing Type" info={EXCALIBUR_COLUMN_INFO.subUseCase} />,
                    cell: e => e.subUseCase,
                    sortingField: "subUseCase",
                    width: 160,
                    minWidth: 160

                },
                {
                    id: "status",
                    header: <ColumnHeader title="Status" infoLabel={EXCALIBUR_COLUMN_INFO.status}
                                          info={<StatusInfo intro={EXCALIBUR_COLUMN_INFO.status} statuses={EXCALIBUR_STATUS_INFO} />} />,
                    cell: e => (
                        <SpaceBetween direction="vertical" size="xxs">
                            <div style={statusColorCodes(e.status)}>{e.status}</div>
                            <Link onFollow={() => navigateToTransactionDetailsPage(e)}>More info</Link>
                        </SpaceBetween>
                    ),
                    sortingField: "status",
                    width: 230,
                    minWidth: 230

                },
                {
                    id: "startTimestamp",
                    header: <ColumnHeader title="Request Created on" info={EXCALIBUR_COLUMN_INFO.startTimestamp} />,
                    cell: e => {return formatDateWithTime(e.startTimestamp)},
                    sortingField: "startTimestamp",
                    width: 230,
                    minWidth: 230
                },
                {
                    id: "createdBy",
                    header: <ColumnHeader title="Submitted By" info={EXCALIBUR_COLUMN_INFO.createdBy} />,
                    cell: e => e.createdBy,
                    sortingField: "createdBy",
                    width: 190,
                    minWidth: 190
                },
                {
                    id: "inputFileKey",
                    header: <ColumnHeader title="Input File" info={EXCALIBUR_COLUMN_INFO.inputFileKey} />,
                    cell: e => {
                        return getFileNameButtonFromS3Key(e.inputFileKey, 'EXCALIBUR_BUCKET',params.domain)
                    },
                    sortingField: "inputFileKey",
                    width: 260,
                    minWidth: 260
                },
                {
                    id: "generatedFileKey",
                    header: <ColumnHeader title="Generated Report" info={EXCALIBUR_COLUMN_INFO.generatedFileKey} />,
                    cell: e => {
                        return getFileNameButtonFromDMS(e.documentId, e.useCase, e.subUseCase, e.inputFileKey, downloadDocumentDispatcher);
                    },
                    width: 260,
                    minWidth: 260
                },
                {
                    id: "errorDetails",
                    header: <ColumnHeader title="Error Details" info={EXCALIBUR_COLUMN_INFO.errorDetails} />,
                    cell: row => renderErrorDetailsWithMoreInfo(row),
                    width: 320,
                    minWidth: 320
                },
                {
                    id: "txnArtifacts",
                    header: <ColumnHeader title="Artifacts" info={EXCALIBUR_COLUMN_INFO.txnArtifacts} />,
                    cell: e => {
                        return (
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="download" onClick={() => downloadArtifacts(e.generatedFileKey)}>View</Button>
                                <Button iconName="upload" variant="primary">
                                    <input className="inputButton hidden" type={"file"} accept={ARTIFACTS_FORMAT}
                                           name="file" onChange={(event) => uploadArtifacts(e, event.target.files)}/>
                                    Upload
                                </Button>
                            </SpaceBetween>
                        )
                    },
                    minWidth: 300
                }
            ]}
            visibleColumns={[
                "useCase",
                "subUseCase",
                "status",
                "startTimestamp",
                "createdBy",
                "inputFileKey",
                "generatedFileKey",
                "errorDetails",
                "txnArtifacts"
            ]}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No files</b>
                    <Box
                        padding={{ bottom: "s" }}
                        variant="p"
                        color="inherit">
                        No files to display.
                    </Box>

                </Box>
            }
            items={getPageItems(table.rows, table.currentPageIndex)}
        />
        </>
    );
}

export default Actiontable;